// Client-side print/export view: renders the whole document (all pages, charts,
// SVG images) with print CSS and triggers the browser's Save-as-PDF. No server
// Browser Rendering required (works on the free tier).

import React from "react";
import { useParams } from "react-router-dom";
import type { ProjectDetail } from "@asmg/shared";
import { api } from "../api/client";
import { ChartView } from "../components/ChartView";
import { Spinner } from "../components/ui";

export function PrintView() {
  const { id = "" } = useParams();
  const [detail, setDetail] = React.useState<ProjectDetail | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    api
      .getProject(id)
      .then(setDetail)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  // Once rendered, give charts a moment to draw, then open the print dialog.
  React.useEffect(() => {
    if (!detail) return;
    const t = setTimeout(() => window.print(), 1200);
    return () => clearTimeout(t);
  }, [detail]);

  if (error) return <div className="container">Error: {error}</div>;
  if (!detail) return <div className="container"><Spinner label="Preparing document…" /></div>;

  const pages = [...detail.pages].sort((a, b) => a.ord - b.ord);

  return (
    <div className="print-root">
      <div className="no-print" style={{ padding: 12, textAlign: "center" }}>
        <button className="btn" onClick={() => window.print()}>Print / Save as PDF</button>
        <span className="muted" style={{ marginLeft: 12 }}>
          Use your browser's “Save as PDF” in the print dialog.
        </span>
      </div>
      {pages.map((p) => {
        const charts = detail.charts.filter((c) => c.pageId === p.id);
        const images = detail.assets.filter((a) => a.pageId === p.id && a.assetType === "image");
        return (
          <section className="print-page" key={p.id}>
            <div dangerouslySetInnerHTML={{ __html: p.contentHtml }} />
            {charts.map((c) => (
              <ChartView key={c.id} projectId={detail.id} chart={c} onRegenerated={() => {}} readOnly />
            ))}
            {images.map((a) => (
              <div className="illus" key={a.id}>
                <img src={api.assetUrl(a.id)} alt={a.description ?? "illustration"} />
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}
